import { useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const saved = new Map<string, number>();

/**
 * Phone web: new pages open at the top; Back/Forward returns to where the
 * reader was. Pages load their data after mounting, so the saved position is
 * retried for a short while until the page is tall enough to reach it.
 */
export function usePhoneScrollRestore(enabled: boolean): void {
  const location = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (!enabled) return;
    const prev = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    return () => { history.scrollRestoration = prev; };
  }, [enabled]);

  useLayoutEffect(() => {
    if (!enabled) return;
    const key = location.key;
    const target = navType === 'POP' ? saved.get(key) ?? 0 : 0;
    let raf = 0;
    const until = performance.now() + 2500;
    const attempt = () => {
      window.scrollTo({ top: target, behavior: 'instant' as ScrollBehavior });
      if (Math.abs(window.scrollY - target) > 2 && performance.now() < until) raf = requestAnimationFrame(attempt);
    };
    attempt();
    const remember = () => saved.set(key, window.scrollY);
    window.addEventListener('scroll', remember, { passive: true });
    // A user scroll stops the retry so we never fight the reader.
    const stop = () => cancelAnimationFrame(raf);
    window.addEventListener('touchstart', stop, { passive: true, once: true });
    window.addEventListener('wheel', stop, { passive: true, once: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', remember);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('wheel', stop);
    };
  }, [enabled, location.key, navType]);
}
